/**
 * 座位块（§11.10 状态编码 / §11.6 动效）。
 *
 * 视觉构造是「圆形头像 + 名牌」，不是一个写着字的圆角矩形：
 *   - 头像默认是昵称首字 + 按昵称哈希出的身份色（8 档渐变，见 table.css 的 data-av）。
 *     用户可以换成一个 emoji，或者上传一张自定义图；机器人使用性格对应的内置头像（`table/avatar.ts` 的
 *     `resolveAvatar`）——两者都会带来当初特意避开的成本（emoji 字形随系统变、
 *     图片要发一次请求），是这次改版里权衡后接受的取舍，不是忘了这条注释。
 *     没换过的账号（`avatarSeed === ''`）行为与以前完全一致。
 *   - 迷你底牌从头像右后方探出来，像真人捏在手里。
 *   - 名牌（昵称 + 筹码）吊在头像下方，自带 surface 底：
 *     毛毡上只准放 --c-text，次要文字与金色数字必须坐在 surface 上（tokens.css）。
 *
 * 状态**绝不只用颜色编码**，每种状态至少两重信号：
 *   弃牌   = 降透明 + 去色 + 「弃」角标
 *   全下   = 金橙环 + "ALL IN" 文字
 *   行动中 = 呼吸辉光环 + 倒计时环 + 名牌提亮 + 名字加粗
 *   断线   = 去色 + 断线图标 + 「离线」文字（危险色小胶囊）
 *   不在局 = 半透明 + 中性胶囊「暂离 / 筹码用完 / 买分中」（见 store 的 `SeatNote`）
 *            ★ 不用危险色：人好好地在，只是这手不打。以前和「离线」同一个红胶囊，
 *              输光了在等买分的 AI 看起来就像掉线了。
 *
 * 倒计时与筹码滚动都不走 React state（红线 #48）：
 * 前者是纯 CSS animation（负 delay 支持中途接入），后者直接写 textContent。
 * 呼吸辉光同理——CSS keyframes 只改 opacity/scale，box-shadow 是预置的静态值（红线 #46）。
 */

import { memo } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import type { CardId, Chips, PlayerHandStatus, SeatIndex } from '@poker/shared'

import { fxKey } from '../fx/FxController.js'
import { useFxNode } from '../fx/FxLayer.js'
import { useRollingNumber } from '../fx/rollNumber.js'
import { IconUser, IconWifiOff } from '../icons/index.js'

import { resolveAvatar } from './avatar.js'
import { Card } from './Card.js'
import { actionText, formatChips } from './cardFormat.js'
import type { SeatLastAction } from './cardFormat.js'
import type { SeatAnchor } from './seatGeometry.js'
import type { SeatNote } from '../store/tableStore.js'
import './table.css'

/**
 * 倒计时环的周长：viewBox 100×100、r=45 的圆，2πr ≈ 282.7。
 * 取整到 283 —— 差的那 0.3 在 40px 的环上是 0.04px，肉眼与截图都不可见。
 */
const RING_LEN = 283

/** 不在局的三种说法。与房间里的名单角标同一套词（room/roomDerive.ts 的 seatBadges） */
const REST_TEXT: Readonly<Record<Exclude<SeatNote, 'offline'>, string>> = {
  away: '暂离',
  broke: '筹码用完',
  buying: '买分中',
}

/** 身份色板档数，必须与 table.css 里 .seat__avatar[data-av='0'..'7'] 的条数一致 */
const AVATAR_TONES = 8

export type { SeatLastAction } from './cardFormat.js'

export interface SeatProps {
  readonly anchor: SeatAnchor
  readonly name: string
  readonly stack: Chips
  /** 见 `table/avatar.ts`：''=默认字母、'emoji:X'=emoji、'custom'=自定义图、'bot:X'=内置 AI 头像 */
  readonly avatarSeed?: string
  /** 自定义头像的地址由它拼出来（`/api/avatars/:accountId`），本地传手机没有账号时留空 */
  readonly accountId?: string
  /** 空座位：只画一个虚线圈占位，不画头像色与筹码 */
  readonly empty?: boolean
  readonly isBot?: boolean
  /** 为什么这人不在牌局里；null / 不传 = 正常在座 */
  readonly note?: SeatNote | null
  /** 本手的参与状态；null = 没参与这手牌 */
  readonly status: PlayerHandStatus | null
  readonly hasCards: boolean
  /** 只有本人的牌、或已对全场亮出的牌才非 null（服务端已裁过，这里是第二道防线） */
  readonly holeCards: readonly [CardId, CardId] | null
  readonly isHero: boolean
  readonly isActing: boolean
  readonly isWinner?: boolean
  readonly lastAction: SeatLastAction | null
  /** 摊牌后的牌型文案，必须由 formatHand 产出，绝不在这里拼字符串 */
  readonly handLabel?: string | null
  /** 最佳五张的高亮集合；非空时不在集合里的牌灰化 */
  readonly bestFive?: ReadonlySet<CardId> | null
  /** 行动截止时刻（服务器时钟）。本地传手机模式恒为 null（turnMs=0，不渲染环）。 */
  readonly deadlineAt?: number | null
  readonly turnMs?: number
  readonly reducedMotion?: boolean
  /**
   * 点了这张名牌要做什么（房间层给的入口：别人的是座位名片，自己的是「我的座位」面板）。
   *
   * 不传就退回纯展示的 `<div>`，DOM 与现在一模一样 —— 牌桌本身不知道有什么动作可做，
   * 那是房间层的事。空座永远不可点（点了没有对象）。
   * ★ 自己那一格以前也永远不可点，注释说「买分、离座都在别处」—— 离座其实哪儿都没有。
   *   现在由房间层决定：有行动权、没轮到自己时才传（面板不能盖住正在倒计时的决定）。
   */
  readonly onSelect?: (seat: SeatIndex) => void
}

function SeatImpl(props: SeatProps): ReactNode {
  const {
    anchor,
    name,
    stack,
    avatarSeed = '',
    accountId = '',
    empty = false,
    isBot = false,
    note = null,
    status,
    hasCards,
    holeCards,
    isHero,
    isActing,
    isWinner = false,
    lastAction,
    handLabel,
    bestFive,
    deadlineAt = null,
    turnMs = 0,
    reducedMotion = false,
    onSelect,
  } = props

  const seatRef = useFxNode(fxKey.seat(anchor.seat))
  const glowRef = useFxNode(fxKey.glow(anchor.seat))
  const bubbleRef = useFxNode(fxKey.bubble(anchor.seat))
  const stackRef = useRollingNumber(stack, { format: formatChips, reduced: reducedMotion })

  const folded = status === 'folded'
  const allIn = status === 'allIn'
  const offline = !empty && note === 'offline'
  // 本手还拿着牌的人照常打（全下时筹码也是 0，不能说成「筹码用完」）
  const rest = empty || hasCards || note === null || note === 'offline' ? null : note

  const classes = ['seat', 'stage-anchor']
  // 气泡/牌型文案贴着舞台上下沿时要翻面：.stage 是 overflow:hidden，
  // 不翻就会被横着切掉一截（几何层已经算好了往哪边翻，这里零判断）
  if (anchor.bubbleBelow) classes.push('seat--bubble-below')
  if (!anchor.handLabelBelow) classes.push('seat--label-above')
  if (isHero) classes.push('seat--hero')
  if (empty) classes.push('seat--empty')
  if (isActing) classes.push('seat--acting')
  if (folded) classes.push('seat--folded')
  if (allIn) classes.push('seat--allin')
  if (isWinner) classes.push('seat--winner')
  if (offline) classes.push('seat--offline')
  if (rest !== null) classes.push('seat--away')

  const style: CSSProperties = { left: `${anchor.box.cx}px`, top: `${anchor.box.cy}px` }

  const avatar = resolveAvatar(avatarSeed, accountId)

  // hero 的底牌画在下方的大手牌区，座位块里再画一份只会让人以为自己有四张牌
  const showMiniCards = !isHero && hasCards && !empty

  // 名牌可点只在「有人坐的座位」上成立；自己那一格给不给由房间层决定（传不传 onSelect）
  const tappable = onSelect !== undefined && !empty

  // 「AI」只画成头像角上的小标记（见下），读屏靠这里念出来
  const label = empty
    ? '空座位'
    : `${name}${isBot ? '（AI）' : ''}，筹码 ${formatChips(stack)}${folded ? '，已弃牌' : ''}${allIn ? '，全下' : ''}${
        offline ? '，离线' : rest !== null ? `，${REST_TEXT[rest]}` : ''
      }`

  return (
    <div className={classes.join(' ')} style={style} role="group" aria-label={label}>
      {/*
        座位重排 FLIP 动的是 .seat__inner 而不是 .seat 本身：
        .seat 是 .stage-anchor，身上那条 translate(-50%,-50%) 是「把百分比锚点变成中心」的
        静态变换。WAAPI 一旦接管 transform 就会整条替换掉它，座位会先跳半个身位、
        动画结束再弹回来。静态变换留在父级、动画只碰子级，是本项目的统一约定。
      */}
      <div className="seat__inner" ref={seatRef}>
        {/* 光晕预置在这里：动画只改它的 opacity/scale，绝不动 box-shadow（红线 #46） */}
        <span className="seat__glow" ref={glowRef} aria-hidden="true" />

        <div className="seat__body">
          <div className="seat__top">
            {/*
              ★ 可点时渲染成 <button>，不可点时仍是 <span>。
                头像是牌桌上最像「这个人」的那个元素，点它看大图是本能动作；
                但它在牌桌上只有 40 来像素，画的是什么根本看不清。
              ★ 可点时不能再 aria-hidden：一个读屏读不到的按钮等于不存在。
                不可点时保持 aria-hidden —— 名字在下面 seat__name 那一份，头像纯装饰。
            */}
            <AvatarBox
              tone={empty ? undefined : avatar.kind === 'bot' ? avatar.variant : avatar.kind === 'default' ? avatarTone(name) : undefined}
              kind={empty ? undefined : avatar.kind === 'default' ? undefined : avatar.kind}
              botVariant={empty || avatar.kind !== 'bot' ? undefined : avatar.variant}
              {...(tappable
                ? { onSelect: () => onSelect?.(anchor.seat), label: isHero ? '我的座位' : `查看 ${name} 的头像` }
                : {})}
            >
              {empty ? (
                <IconUser size={16} />
              ) : avatar.kind === 'custom' || avatar.kind === 'bot' ? (
                // alt="" 是对的：这个 span 已经 aria-hidden，头像本身不承载信息，
                // 真正的可访问名字在下面 seat__name 那一份
                <img className="seat__avatar-img" src={avatar.url} alt="" width={64} height={64} />
              ) : avatar.kind === 'emoji' ? (
                avatar.char
              ) : (
                initialOf(name)
              )}
              {/* 倒计时环套在头像外圈；本地传手机模式 turnMs=0 → 整体不渲染（§11.5 最后一条） */}
              {isActing && deadlineAt !== null && turnMs > 0 && (
                <TurnRing deadlineAt={deadlineAt} turnMs={turnMs} />
              )}
              {/*
                ★ AI 标记挂在头像左下角，不再接在名字后面：名牌里只有六十来像素，
                  「 · AI」把四个字的 AI 名字截成「心软阿珍 ·…」—— 名字才是这一格最该读全的东西。
                  纯视觉（读屏在座位的 aria-label 里念「AI」）。
              */}
              {isBot && !empty && (
                <span className="seat__ai" aria-hidden="true">
                  AI
                </span>
              )}
            </AvatarBox>

            {showMiniCards && (
              <div className="seat__cards" aria-hidden={holeCards === null ? true : undefined}>
                <div className="seat__card-a">
                  <Card
                    card={holeCards?.[0] ?? null}
                    faceUp={holeCards !== null}
                    size="seat"
                    fxName={fxKey.hole(anchor.seat, 0)}
                    dim={dimOf(bestFive, holeCards?.[0])}
                    best={bestOf(bestFive, holeCards?.[0])}
                  />
                </div>
                <div className="seat__card-b">
                  <Card
                    card={holeCards?.[1] ?? null}
                    faceUp={holeCards !== null}
                    size="seat"
                    fxName={fxKey.hole(anchor.seat, 1)}
                    dim={dimOf(bestFive, holeCards?.[1])}
                    best={bestOf(bestFive, holeCards?.[1])}
                  />
                </div>
              </div>
            )}
          </div>

          <SeatMeta
            {...(tappable
              ? { onSelect: () => onSelect?.(anchor.seat), label: isHero ? `${name}，我的座位` : `${name}，查看操作` }
              : {})}
          >
            <span className="seat__name">{empty ? '空位' : name}</span>
            {!empty && (
              <span className="seat__stack num" ref={stackRef}>
                {formatChips(stack)}
              </span>
            )}
          </SeatMeta>
        </div>

        {/* 角标：文字 + 图标 + 颜色三重编码，绝不只靠颜色 */}
        {folded && <span className="seat__badge seat__badge--fold">弃</span>}
        {allIn && <span className="seat__badge seat__badge--allin">ALL IN</span>}
        {offline && (
          <span className="seat__badge seat__badge--off">
            <IconWifiOff size={11} />
            离线
          </span>
        )}
        {rest !== null && <span className="seat__badge seat__badge--rest">{REST_TEXT[rest]}</span>}

        {/* 气泡同理：居中用的 translateX(-50%) 挂在 wrap 上，动画只动里层 */}
        {lastAction !== null && (
          <span className="seat__bubble-wrap" aria-hidden="true">
            <span className="seat__bubble" ref={bubbleRef}>
              {actionText(lastAction.kind, lastAction.amount)}
            </span>
          </span>
        )}

        {handLabel != null && handLabel !== '' && <span className="seat__hand">{handLabel}</span>}
      </div>
    </div>
  )
}

/**
 * 名牌。可点时是 `<button>`，不可点时是 `<div>` —— 同一个类、同一套样式。
 *
 * 不做成「永远是 button、不可点时 disabled」：牌桌上大半的座位都不可点，
 * 一屏八颗 disabled 按钮会把读屏的焦点序列塞满，而它们一个都按不动。
 */
/**
 * 头像外框。可点时是按钮，否则是纯展示的 span。
 *
 * ★ 类名与 data-* 两种形态完全一致 —— 那一整套视觉（身份色、呼吸环、倒计时环）
 *   都挂在 `.seat__avatar` 上，换了元素类型但不能换类名。
 * ★ 按钮要做一次彻底的外观重置（见 table.css 的 .seat__avatar--tap），
 *   否则浏览器默认边框会把那个圆压扁。
 */
function AvatarBox({
  tone,
  kind,
  botVariant,
  onSelect,
  label,
  children,
}: {
  readonly tone?: number
  readonly kind?: string
  readonly botVariant?: number
  readonly onSelect?: () => void
  readonly label?: string
  readonly children: ReactNode
}): ReactNode {
  if (onSelect === undefined) {
    return (
      <span className="seat__avatar" data-av={tone} data-av-kind={kind} data-bot-variant={botVariant} aria-hidden="true">
        {children}
      </span>
    )
  }
  return (
    <button
      type="button"
      className="seat__avatar seat__avatar--tap"
      data-av={tone}
      data-av-kind={kind}
      data-bot-variant={botVariant}
      aria-label={label}
      onClick={onSelect}
    >
      {children}
    </button>
  )
}

function SeatMeta({
  onSelect,
  label,
  children,
}: {
  readonly onSelect?: () => void
  readonly label?: string
  readonly children: ReactNode
}): ReactNode {
  if (onSelect === undefined) return <div className="seat__meta">{children}</div>
  return (
    <button type="button" className="seat__meta seat__meta--tap" aria-label={label} onClick={onSelect}>
      {children}
    </button>
  )
}

/**
 * 头像首字。用 `Array.from` 而不是 `name[0]`：
 * 昵称里出现 emoji 或代理对时，按 UTF-16 取第一个码元会切出半个字符（乱码方块）。
 * 拉丁字母转大写，中日韩字符 toUpperCase 是恒等变换，不必分支。
 */
function initialOf(name: string): string {
  const first = Array.from(name.trim())[0]
  return first === undefined ? '?' : first.toUpperCase()
}

/**
 * 昵称 → 身份色档位。要求**同名恒同色**（换手牌、重连、换设备都不能变色），
 * 所以只能是纯函数哈希，不能用座位号（换座就变色）也不能用随机数（红线 #15 禁 Math.random）。
 */
function avatarTone(name: string): number {
  let h = 0
  for (let i = 0; i < name.length; i++) {
    // djb2 变体：位运算保证落在 32 位无符号区间，不同长度的昵称也散得开
    h = (h * 31 + name.charCodeAt(i)) >>> 0
  }
  return h % AVATAR_TONES
}

function dimOf(best: ReadonlySet<CardId> | null | undefined, card: CardId | undefined): boolean {
  return best != null && best.size > 0 && card !== undefined && !best.has(card)
}
function bestOf(best: ReadonlySet<CardId> | null | undefined, card: CardId | undefined): boolean {
  return best != null && card !== undefined && best.has(card)
}

/**
 * 倒计时环。整环用 CSS animation 驱动，**每帧零 JS**（红线 #48）：
 * duration 写进变量、负 delay 让中途接入的客户端直接跳到正确进度、fill-mode forwards
 * 保证走完停在 0（而不是弹回满格）。客户端绝不本地判超时自己弃牌（§11.5）。
 *
 * stroke-dashoffset 是 paint-only 属性，不触发 layout；环的起点靠 CSS 的 rotate(-90deg)
 * 转到 12 点方向（转在容器上，动画只碰 dashoffset）。
 */
function TurnRing({ deadlineAt, turnMs }: { deadlineAt: number; turnMs: number }): ReactNode {
  const remain = Math.max(0, deadlineAt - Date.now())
  const elapsed = Math.max(0, turnMs - remain)
  if (remain <= 0) return null
  const style = {
    '--ring-len': `${RING_LEN}`,
    '--ring-dur': `${turnMs}ms`,
    '--ring-delay': `${-elapsed}ms`,
  } as CSSProperties
  return (
    <svg className="seat__timer" viewBox="0 0 100 100" aria-hidden="true">
      <circle cx="50" cy="50" r="45" style={style} />
    </svg>
  )
}

export const Seat = memo(SeatImpl)
